/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { FormikProps } from 'formik';
import { ApiExercise } from '@codey2/codey-api-types/models';
import ContentContainer from '../../components/common/ContentContainer/ContentContainer';
import styles from './ExercisePage.module.css';
import { Header } from '../../components/common/Header';
import { TabBar } from '../../components/common/TabPanel';
import { DetailsTab } from './components/DetailsTab';
import { StepsTab } from './components/StepsTab';
import { AssetsTab } from './components/AssetsTab';
import { ActionBar, Action } from '../../components/common/ActionBar';
import { useExerciseForm, FormContent } from './useExerciseForm';
import { useExercisesApi } from '../../app/api/useExercisesApi';
import { useSnackbar } from '../../navigation/snackbar';
import { BackButton } from '../../components/common/BackButton/BackButton';
import { GeneratedExercise } from '../../app/hooks/useExerciseGenerator';

type TabKey = 'details' | 'steps' | 'assets';
const PAGE_TABS: { id: TabKey; label: string }[] = [
  { id: 'details', label: 'Algemene informatie' },
  { id: 'steps', label: 'Stappen' },
  { id: 'assets', label: 'Bestanden' },
];

export const ExercisePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { getExerciseById } = useExercisesApi();
  const { showSnackbar } = useSnackbar();

  const [exercise, setExercise] = useState<ApiExercise | undefined>();
  const [isLoadingExercise, setIsLoadingExercise] = useState(false);
  const [selectedTabId, setSelectedTabId] = useState<TabKey>('details');

  const formik = useExerciseForm({ exercise });
  const isEditMode = !!exercise;

  // Load generated exercise from location state
  useEffect(() => {
    const state = location.state as { generatedExercise?: GeneratedExercise };
    if (state?.generatedExercise && !id) {
      const generated = state.generatedExercise;
      formik.setValues({
        name: generated.title,
        description: generated.introduction,
        platform: generated.platform as 'SCRATCH' | 'MICROBIT' | 'COSPACES',
        steps: generated.steps.map((step, index) => ({
          id: crypto.randomUUID(),
          label: step.name,
          content: step.content,
        })),
      });
      // Clear the location state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    if (id) {
      const loadExercise = async () => {
        setIsLoadingExercise(true);
        try {
          const fetchedExercise = await getExerciseById(Number(id));
          setExercise(fetchedExercise);
        } catch {
          showSnackbar('Opdracht kon niet worden geladen', 'error');
        } finally {
          setIsLoadingExercise(false);
        }
      };
      loadExercise();
    }
  }, [id, getExerciseById, showSnackbar]);

  const actions: Action[] = [{ key: 'save', type: 'primary', buttonType: 'submit', label: 'Opslaan' }];

  const handleAction = (actionKey: string) => {
    if (actionKey === 'save') {
      formik.handleSubmit();
    }
  };

  if (isLoadingExercise) {
    return (
      <ContentContainer>
        <div className={styles.exercisePage}>
          <div className={styles.loadingSpinner}>
            <div className={styles.spinner}></div>
            <p>Opdracht laden...</p>
          </div>
        </div>
      </ContentContainer>
    );
  }

  return (
    <>
      <ContentContainer>
        <div className={styles.exercisePage}>
          <Header
            size={2}
            content={
              <div className={styles.headerContent}>
                {isEditMode ? 'Opdracht bewerken' : 'Nieuwe opdracht'} <BackButton to="/opdrachten" />
              </div>
            }
          />
          <TabBar selectedTab={selectedTabId} onSelectedTabChange={tabId => setSelectedTabId(tabId as TabKey)} tabs={PAGE_TABS} />

          <div className={styles.detailsSection}>
            {selectedTabId === 'details' && <DetailsTab formik={formik} />}
            {selectedTabId === 'steps' && <StepsTab formik={formik} />}
            {selectedTabId === 'assets' && <AssetsTab exerciseId={exercise?.id} />}
          </div>
        </div>
      </ContentContainer>
      <ActionBar actions={actions} onAction={handleAction} />
    </>
  );
};

export interface TabProps {
  formik: FormikProps<FormContent>;
}
